import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { initPostHog } from "./lib/posthog.ts";
import DOMPurify from "dompurify";

// Global DOMPurify hook to protect against reverse tabnabbing
// Applies rel="noopener noreferrer" to all target="_blank" links globally across the app
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    const currentRel = node.getAttribute("rel") || "";
    if (!currentRel.includes("noopener")) {
      node.setAttribute("rel", (currentRel + " noopener noreferrer").trim());
    }
  }
});

// Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: "https://535faeda3a560a8f3564eda0fb36cf1c@o4511302480953344.ingest.us.sentry.io/4511302486654976",
  // Setting this option to true will send default PII data to Sentry.
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration()
  ],
  tracesSampleRate: 0.05,
  tracePropagationTargets: ["localhost", /^https:\/\/cxtuyntqsbcgbtbzujof\.supabase\.co/],
  // Filter noisy errors that don't represent real bugs
  ignoreErrors: [
    // Browser extensions / non-actionable
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
    // Network blips that are user-side, not our bug
    "NetworkError when attempting to fetch resource",
    "Failed to fetch",
    "Load failed",
    // Common browser/extension noise
    "Script error.",
    "ChunkLoadError",
  ],
  beforeSend(event, hint) {
    // Wrap non-Error promise rejections into real Errors so stack traces are usable.
    const original = hint?.originalException;
    if (original && typeof original === "object" && !(original instanceof Error)) {
      const wrapped = new Error(
        `Non-Error rejection: ${JSON.stringify(original).slice(0, 500)}`
      );
      hint.originalException = wrapped;
    }

    // Drop fetch failures caused by ad blockers / network blocks to third-party
    // services (RevenueCat, PostHog, Sentry itself, Google). These are user-side
    // (uBlock, EasyPrivacy, corporate proxies) and not actionable for us.
    const err = hint?.originalException as Error | undefined;
    const msg = err?.message || (typeof event.message === "string" ? event.message : "");
    if (msg && /Failed to fetch|NetworkError|Load failed/i.test(msg)) {
      const blob = JSON.stringify(event).toLowerCase();
      if (/revenuecat|posthog|sentry\.io|google-analytics|googletagmanager|facebook\.com\/tr/.test(blob)) {
        return null;
      }
    }
    return event;
  },
});

// Initialize PostHog for analytics
initPostHog();

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}

